/**
 * Wizard card for the ga4_property step (Slice 6 — first attribution slice).
 *
 * The admin enters the client's GA4 Account ID; `create_property` provisions a
 * GA4 property + web data stream under that account (granted-access model) and
 * stores the Measurement ID under `seo_google_analytics_id`, which wires it into
 * the viewer's GTM layer. mainDomain + timezone come from the run's params, so
 * they aren't re-entered.
 *
 * No secret is handled here — the Google credential is a platform service
 * account, and the Account ID / Measurement ID are identifiers, not credentials.
 * So the Account ID reads back into the field on refresh. The granted-access
 * requirement surfaces as a `waiting_on_client` task with a readable message
 * (incl. the service-account email) via StepCard's error slot.
 */
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { StepCard } from './StepCard'

interface Ga4PropertyCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface Ga4Result {
  measurementId?: string
  propertyId?: string
  streamId?: string
  displayName?: string
  reusedProperty?: boolean
  reusedStream?: boolean
}

const formSchema = z.object({
  ga4AccountId: z
    .string()
    .trim()
    .min(1, 'GA4 Account ID is required')
    .regex(/^\d+$/, 'GA4 Account ID must be numeric'),
})

type FormData = z.infer<typeof formSchema>

export function Ga4PropertyCard({
  task,
  accountId,
  runId,
}: Ga4PropertyCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as Ga4Result
  const isDone = task.status === 'done'

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { ga4AccountId: '' },
  })

  // Read back saved values so the field + IDs survive a refresh. None are
  // secret, so the Account ID repopulates into the field.
  const { data: saved } = useQuery({
    queryKey: ['onboarding-ga4-settings', accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', accountId)
        .in('key', [
          'ga4_account_id',
          'seo_google_analytics_id',
          'ga4_property_id',
          'ga4_stream_id',
        ])
      if (error) throw new Error(error.message)
      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.value != null) map[row.key] = row.value
      }
      return map
    },
  })

  // Seed the Account ID once when saved settings first load.
  const seeded = useRef(false)
  useEffect(() => {
    if (!saved || seeded.current) return
    seeded.current = true
    form.reset({ ga4AccountId: saved['ga4_account_id'] ?? '' })
  }, [saved, form])

  const measurementId =
    result.measurementId ?? saved?.['seo_google_analytics_id'] ?? ''
  const propertyId = result.propertyId ?? saved?.['ga4_property_id'] ?? ''
  const streamId = result.streamId ?? saved?.['ga4_stream_id'] ?? ''

  const createProperty = form.handleSubmit((values) => {
    invokeStep.mutate(
      {
        functionName: 'onboard-ga4',
        accountId,
        params: { action: 'create_property', ga4AccountId: values.ga4AccountId },
      },
      {
        onError: (error) =>
          toast({
            title: 'GA4 provisioning failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  })

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            <strong>Before running:</strong> in Google Analytics → Admin →
            Account Access Management, add the platform service-account email
            (shown in the error message after the first attempt) with the{' '}
            <strong>Editor</strong> role on the client&rsquo;s GA4 account. The
            property is created under the client&rsquo;s account — they own it.
          </p>
          <p>
            The platform GCP project must have the{' '}
            <strong>Google Analytics Admin API</strong> enabled.
          </p>
          <p>
            Manual path: create a GA4 property + web data stream in GA Admin and
            paste the Measurement ID into Settings &gt; SEO &amp; Marketing &gt;
            Google Analytics ID.
          </p>
        </div>
      }
    >
      {isDone ? (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              GA4 property provisioned — Measurement ID{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {measurementId || '—'}
              </code>
            </span>
          </div>
          {(propertyId || streamId) && (
            <p className="text-xs text-muted-foreground">
              Property {propertyId || '—'} · Stream {streamId || '—'}
              {result.reusedProperty || result.reusedStream
                ? ' (reused existing)'
                : ''}
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => createProperty()}
            disabled={invokeStep.isPending}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-run / reconcile
          </Button>
        </div>
      ) : (
        <form onSubmit={createProperty} className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Grant the platform service account Editor access on the
            client&rsquo;s GA4 account first (see Manual fallback), then create
            the property.
          </p>
          <div className="space-y-1">
            <Label htmlFor="ga4-account">GA4 Account ID</Label>
            <Input
              id="ga4-account"
              placeholder="e.g. 123456789"
              {...form.register('ga4AccountId')}
            />
            {form.formState.errors.ga4AccountId && (
              <p className="text-xs text-red-600">
                {form.formState.errors.ga4AccountId.message}
              </p>
            )}
          </div>
          <Button type="submit" size="sm" disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Create GA4 property
          </Button>
        </form>
      )}
    </StepCard>
  )
}
